import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { COUNTRY_CODES } from "../lib/countryCodes";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ArrowRight, CheckCircle } from "@phosphor-icons/react";

function errText(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

const inp = "mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream focus:outline-none focus:border-papaya";
const primaryBtn = "w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50";

export default function RecoverIdentifier() {
  const [mode, setMode] = useState(null); // username | email | phone
  const [recovered, setRecovered] = useState(false);

  if (recovered) return <RecoveredScreen />;

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5 py-10 grain">
      <div className="relative z-10 w-full max-w-md crystal-glass rounded-3xl p-8 sm:p-10 animate-fade-up">
        <Link to="/login" data-testid="recid-back" className="inline-flex items-center gap-2 text-cream/60 hover:text-cream text-xs tracking-[0.2em] uppercase font-bold mb-4">
          <ArrowLeft size={14} /> Back to login
        </Link>
        <p className="text-papaya text-xs tracking-[0.3em] uppercase font-bold">Account recovery</p>
        <h1 className="text-cream text-4xl font-black tracking-tighter mt-1 leading-none">Let's help you<br />recover your account.</h1>
        <p className="text-cream/60 mt-3 text-sm">Choose something you still remember.</p>

        {!mode && (
          <div className="mt-7 space-y-3">
            <ChooserBtn testid="recid-choose-username" label="I remember my username" hint="We'll show masked hints of the email/phone on file" onClick={() => setMode("username")} />
            <ChooserBtn testid="recid-choose-phone" label="I remember my phone number" hint="Verify by SMS to recover access" onClick={() => setMode("phone")} />
            <ChooserBtn testid="recid-choose-email" label="I remember my email address" hint="We'll email your username" onClick={() => setMode("email")} />
          </div>
        )}

        {mode === "username" && <ByUsername onBack={() => setMode(null)} onRecovered={() => setRecovered(true)} />}
        {mode === "phone" && <ByPhone onBack={() => setMode(null)} onRecovered={() => setRecovered(true)} />}
        {mode === "email" && <ByEmail onBack={() => setMode(null)} />}
      </div>
    </div>
  );
}

function ChooserBtn({ label, hint, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full text-left rounded-2xl bg-white/5 border border-white/10 hover:border-papaya p-4 transition-colors">
      <p className="text-cream font-black text-sm">{label}</p>
      <p className="text-cream/50 text-xs mt-1">{hint}</p>
    </button>
  );
}

function BackLink({ onBack }) {
  return (
    <button type="button" onClick={onBack} data-testid="recid-sub-back" className="text-cream/50 text-xs flex items-center gap-1 hover:text-papaya mb-2">
      <ArrowLeft size={14} /> Other options
    </button>
  );
}

function ByUsername({ onBack, onRecovered }) {
  const [username, setUsername] = useState("");
  const [hints, setHints] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setHints(null);
    try {
      const { data } = await api.post("/auth/recover/identifier", { username });
      setHints(data);
    } catch (e) { setErr(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-7">
      <BackLink onBack={onBack} />
      {!hints ? (
        <form onSubmit={lookup} className="space-y-4">
          <label className="block">
            <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">Username</span>
            <input data-testid="recid-username-input" required value={username} onChange={(e) => setUsername(e.target.value)} placeholder="yourname" className={inp} />
          </label>
          {err && <p data-testid="recid-username-error" className="text-red-400 text-sm">{err}</p>}
          <button data-testid="recid-username-submit" type="submit" disabled={busy} className={primaryBtn}>
            {busy ? "Looking…" : <>Continue <ArrowRight size={16} weight="bold" /></>}
          </button>
        </form>
      ) : (
        <div data-testid="recid-hints">
          <p className="text-cream/70 text-sm">We found <span className="text-papaya font-bold">@{hints.username}</span>. Recovery methods on file:</p>
          <div className="mt-3 space-y-2">
            {hints.masked_email && <div className="rounded-2xl bg-white/5 border border-white/10 p-3 text-cream text-sm" data-testid="recid-masked-email">✉️ {hints.masked_email}</div>}
            {hints.masked_phone && <div className="rounded-2xl bg-white/5 border border-white/10 p-3 text-cream text-sm" data-testid="recid-masked-phone">📱 {hints.masked_phone}</div>}
          </div>
          {hints.has_phone ? (
            <div className="mt-4">
              <p className="text-cream/50 text-xs mb-2">Verify your phone to recover access:</p>
              <ByPhone onBack={onBack} onRecovered={onRecovered} compact />
            </div>
          ) : (
            <p className="mt-4 text-cream/60 text-sm">Head to <Link to="/recover" className="text-papaya font-bold">Forgot password</Link> and use the email hint above to reset your password.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ByEmail({ onBack }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/recover/username", { email });
      setDone(true);
    } catch (e) { setErr(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-7">
      <BackLink onBack={onBack} />
      {done ? (
        <div className="rounded-2xl bg-jam-green/15 border border-jam-green/30 p-4" data-testid="recid-email-done">
          <p className="text-cream text-sm">If an account is associated with that email, we've sent the username to it.</p>
          <Link to="/login" className="mt-4 block text-center text-cream/60 text-xs tracking-[0.2em] uppercase hover:text-papaya">Return to login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">Email address</span>
            <input data-testid="recid-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inp} />
          </label>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button data-testid="recid-email-submit" type="submit" disabled={busy} className={primaryBtn}>
            {busy ? "Sending…" : <>Email me my username <ArrowRight size={16} weight="bold" /></>}
          </button>
        </form>
      )}
    </div>
  );
}

function ByPhone({ onBack, onRecovered, compact }) {
  const { refresh } = useAuth();
  const [cc, setCc] = useState(0);
  const [number, setNumber] = useState("");
  const [step, setStep] = useState("number");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const fullPhone = () => `${COUNTRY_CODES[cc].code}${number.replace(/[^0-9]/g, "")}`;

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/recover/phone/send", { phone: fullPhone() });
      setStep("code"); setCooldown(data?.resend_in || 30);
    } catch (e) { setErr(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/recover/phone/verify", { phone: fullPhone(), code });
      await refresh();
      onRecovered();
    } catch (e) { setErr(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={compact ? "" : "mt-7"}>
      {!compact && <BackLink onBack={onBack} />}
      {step === "number" ? (
        <form onSubmit={send} className="space-y-4">
          <div className="flex gap-2">
            <select data-testid="recid-country" value={cc} onChange={(e) => setCc(Number(e.target.value))}
              className="bg-white/5 border border-white/10 rounded-2xl px-2 py-3 text-cream text-sm focus:outline-none focus:border-papaya max-w-[42%]">
              {COUNTRY_CODES.map((c, i) => <option key={`${c.name}${i}`} value={i} className="bg-ink">{c.flag} {c.code} {c.name}</option>)}
            </select>
            <input data-testid="recid-phone-input" type="tel" required value={number} onChange={(e) => setNumber(e.target.value)} placeholder="876 555 1234" className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream focus:outline-none focus:border-papaya" />
          </div>
          {err && <p data-testid="recid-phone-error" className="text-red-400 text-sm">{err}</p>}
          <button data-testid="recid-phone-send" type="submit" disabled={busy} className={primaryBtn}>
            {busy ? "Sending…" : <>Send code <ArrowRight size={16} weight="bold" /></>}
          </button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-4">
          <p className="text-cream/70 text-sm">Enter the code sent to <span className="text-papaya font-bold">{fullPhone()}</span>.</p>
          <input data-testid="recid-otp-input" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="123456" className={inp} />
          {err && <p data-testid="recid-otp-error" className="text-red-400 text-sm">{err}</p>}
          <button data-testid="recid-otp-verify" type="submit" disabled={busy} className={primaryBtn}>
            {busy ? "Verifying…" : <>Verify & recover <ArrowRight size={16} weight="bold" /></>}
          </button>
          <button type="button" data-testid="recid-otp-resend" onClick={send} disabled={busy || cooldown > 0} className="w-full text-center text-cream/60 text-xs hover:text-papaya disabled:opacity-40">
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        </form>
      )}
    </div>
  );
}

function RecoveredScreen() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5 py-10 grain">
      <div className="relative z-10 w-full max-w-md crystal-glass rounded-3xl p-8 sm:p-10 text-center animate-fade-up" data-testid="recid-recovered">
        <CheckCircle size={64} weight="fill" className="text-jam-green mx-auto" />
        <h1 className="text-cream text-4xl font-black tracking-tighter mt-4 leading-none">Account<br />recovered</h1>
        <p className="text-cream/60 mt-3 text-sm">Your account recovery was successful.</p>
        <button data-testid="recid-login-now" onClick={() => nav("/")} className={`${primaryBtn} mt-7`}>Log in now <ArrowRight size={16} weight="bold" /></button>
      </div>
    </div>
  );
}
